/**
 * STXND — ABConvert price-test display bridge.
 *
 * ABConvert runs its price tests through a Shopify Function (cart transform). A function can only
 * change what a cart line is *charged*; it cannot change `variant.price` or
 * `variant.compare_at_price` as Liquid renders them. So every server-rendered price on the
 * storefront still shows catalog data, and ABConvert patches it client-side using the selectors in
 * its app settings. Two gaps are left on this store:
 *
 *   1. The PDP price is built in Replo — `{{ reploSelectedVariant.compare_at_price | ...
 *      | money_without_trailing_zeros }}` inside an `.alchemy-rte` whose only classes are
 *      per-publish hashes (`r-2r68qw`). There is no stable selector to hand ABConvert, so its
 *      `priceTagInProductPageClass` / `compareAtPriceTagInProductPageClass` settings are blank and
 *      the PDP never updates. We locate the nodes by matching their rendered text against the
 *      control group's price / compare-at instead, which survives a Replo re-publish.
 *
 *   2. Cart lines render `item.variant.compare_at_price` straight from the catalog
 *      (`snippets/cart-products.liquid`), so a transformed $100 line keeps a struck-through $150.
 *
 * It also hides any compare-at that is not actually above the selling price: ABConvert writes
 * compare == price when a test group carries no discount, which both the theme and ABConvert's own
 * card markup would otherwise render as a strikethrough identical to the price.
 *
 * Built on ABConvert's public surface (`abconvert.utils.onReady`, `abconvert.user.getExperiments`,
 * `abconvert.user.getAssignedGroup`), with the localStorage contract from their customization guide
 * as the fallback so a first paint does not have to wait on their runtime.
 *
 * Assumes the shop money format groups thousands with "," and separates decimals with "." —
 * true for this store (`${{amount}}`, USD). Replacement text reuses each node's own currency
 * symbol, placement and decimal precision, so `$80` stays `$100` and `$150.00` stays `$250.00`.
 */
(function () {
  'use strict';

  var DICT_KEY = 'abconvert-price-cart-transform-test-dict';
  var GROUP_KEY = 'abconvert-experiment-to-group-index';

  /* Text that is *only* a money value — "$80", "$150.00", "$1,234.56 USD". A currency marker is
     required and "%" disqualifies, so prose ("Free shipping over $100"), the savings badge
     ("SAVE 47%") and bare carousel digits ("1", "2") are all excluded. */
  var MONEY_SHAPE = /^[^\d%]{0,8}\d[\d.,]*[^\d%]{0,8}$/;
  var CURRENCY = /[$\u20ac\u00a3\u00a5\u20a9\u20b9\u00a2]|\b[A-Z]{3}\b/;

  /* "SAVE 47%", "47% OFF" — Replo's discount badge, which is stale for any group whose
     compare-at we change. */
  var PERCENT_BADGE = /^[^\d]{0,12}\d{1,3}\s*%[^\d]{0,12}$/;

  function isMoneyText(text) {
    return text.length < 24 && MONEY_SHAPE.test(text) && CURRENCY.test(text);
  }

  /* ---------- money helpers ---------- */

  function parseMoney(text) {
    if (text == null) return NaN;
    var match = String(text).match(/\d[\d.,]*/);
    if (!match) return NaN;
    return parseFloat(match[0].replace(/,/g, ''));
  }

  /** Render `dollars` using the currency symbol, placement and precision already in `sample`. */
  function formatLike(sample, dollars) {
    var value = Number(dollars);
    if (!isFinite(value)) return null;

    var text = String(sample);
    var match = text.match(/\d[\d.,]*/);
    if (!match) return null;

    var showCents = /\.\d{2}$/.test(match[0]);
    if (!showCents) value = Math.round(value);

    var body = value.toLocaleString('en-US', {
      minimumFractionDigits: showCents ? 2 : 0,
      maximumFractionDigits: showCents ? 2 : 0
    });

    return text.slice(0, match.index) + body + text.slice(match.index + match[0].length);
  }

  /** The first text node carrying a digit, so we write through Replo's inner `<span>` not over it. */
  function moneyTextNode(el) {
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    var node;
    while ((node = walker.nextNode())) {
      if (/\d/.test(node.nodeValue)) return node;
    }
    return null;
  }

  /**
   * Two savings-percentage conventions, deliberately not unified — each has to match whatever
   * rendered the badge it is overwriting, or the control group stops being a no-op.
   *
   * Replo rounds to nearest: its control badge for $80 from $150 reads "SAVE 47%" (46.67).
   * The theme's upsell truncates, because Liquid's `divided_by` on integer cents is integer
   * division: `(15000 - 8000) * 100 / 15000` renders 46, not 47.
   */
  function savePercentReplo(price, compareAt) {
    return Math.round((1 - price / compareAt) * 100);
  }

  function savePercentTheme(price, compareAt) {
    return Math.floor(((compareAt - price) * 100) / compareAt);
  }

  function setPercent(el, percent) {
    var node = moneyTextNode(el);
    if (!node) return;
    node.nodeValue = node.nodeValue.replace(/\d{1,3}/, String(percent));
    el.setAttribute('data-stxnd-abc-applied', el.textContent.trim());
  }

  function setMoney(el, dollars) {
    var node = moneyTextNode(el);
    if (!node) return;
    var next = formatLike(node.nodeValue, dollars);
    if (next == null) return;
    if (next !== node.nodeValue) node.nodeValue = next;
    /* Stamped even when unchanged: it tells the veil observer this value is ours and already
       correct, so a re-render of the same text is not mistaken for Replo writing catalog data. */
    var stamp = next.trim();
    if (el.getAttribute('data-stxnd-abc-applied') !== stamp) {
      el.setAttribute('data-stxnd-abc-applied', stamp);
    }
  }

  function hide(el) {
    if (el.getAttribute('data-stxnd-abc-hidden')) return;
    el.setAttribute('data-stxnd-abc-hidden', 'true');
    el.style.setProperty('display', 'none', 'important');
  }

  function show(el) {
    if (!el.getAttribute('data-stxnd-abc-hidden')) return;
    el.removeAttribute('data-stxnd-abc-hidden');
    el.style.removeProperty('display');
  }

  /**
   * Hide a compare-at together with the "Regular price" label that introduces it — the theme keeps
   * that label in a `.visually-hidden` sibling, so hiding the price alone leaves a screen reader
   * announcing a regular price with no value after it.
   */
  function compareTarget(el) {
    var group = el.closest('.price-item__group');
    /* Only when the group exists purely to wrap this compare-at, never one holding the sale price. */
    if (group && !group.querySelector('.price-item--sale')) return group;
    return el;
  }

  function hideCompare(el) {
    var target = compareTarget(el);
    if (target === el) {
      var label = el.previousElementSibling;
      if (label && label.classList.contains('visually-hidden')) hide(label);
    }
    hide(target);
  }

  function showCompare(el) {
    var target = compareTarget(el);
    if (target === el) {
      var label = el.previousElementSibling;
      if (label && label.classList.contains('visually-hidden')) show(label);
    }
    show(target);
  }

  /* ---------- the anti-flicker veil (snippets/stxnd-abconvert-price.liquid) ---------- */

  /** Hand one node back, right before we set our own styles on it. */
  function releaseVeil(el) {
    var veil = window.STXNDAbcVeil;
    if (veil && veil.release) veil.release(el);
  }

  /** Hand the whole page back — nothing more is going to change. */
  function stopVeil() {
    var veil = window.STXNDAbcVeil;
    if (veil && veil.stop) veil.stop();
  }

  /* ---------- ABConvert data ---------- */

  function readJSON(key) {
    try {
      return JSON.parse(localStorage.getItem(key));
    } catch (error) {
      return null;
    }
  }

  function testDict() {
    return window.abconvertPriceCartTransformTestDict || readJSON(DICT_KEY);
  }

  /**
   * ABConvert's preview widget switches groups by rewriting the stored assignment, while
   * `getAssignedGroup()` goes on reporting the group this visitor was actually rolled into. So
   * during a preview the two disagree, and the API is not the one the tester is steering. Their own
   * card rewriting follows the storage, so preferring the API left a Control preview rendering
   * Variant A's price on the PDP while the cards on the same visit rendered Control's.
   *
   * The widget is only on the page while someone is previewing. Real traffic never sees it, keeps
   * the API as the authority, and is unaffected by this.
   */
  function previewing() {
    if (document.querySelector('.abconvert-widget')) return true;

    /* Their widget renders well after our first pass, which is too late — by then we have already
       written a price from the API's group. A test still in Preview is not running for anybody, so
       a visitor seeing its prices at all is previewing it, and that is readable from the dict on
       the very first pass. Once the test starts, this goes quiet and the widget is the signal. */
    var dict = testDict();
    var experiments = dict && dict.experiments;

    for (var id in experiments) {
      if (Object.prototype.hasOwnProperty.call(experiments, id) &&
          experiments[id] && experiments[id].status === 'Preview') {
        return true;
      }
    }

    return false;
  }

  /** experimentId -> assigned group index. Prefers the live API, falls back to stored assignment. */
  function assignedGroups() {
    var api = window.abconvert;
    var groups = {};

    if (!previewing() && api && api.user && typeof api.user.getExperiments === 'function') {
      try {
        (api.user.getExperiments() || []).forEach(function (experiment) {
          var group = api.user.getAssignedGroup(experiment.id);
          if (group && typeof group.index === 'number') groups[experiment.id] = group.index;
        });
        if (Object.keys(groups).length) return groups;
      } catch (error) {
        /* fall through to the documented storage contract */
      }
    }

    return readJSON(GROUP_KEY) || {};
  }

  /**
   * variantId -> { price, compareAt, controlPrice, controlCompareAt } in dollars for the group this
   * visitor is actually in. Variants outside an assigned experiment are omitted entirely.
   */
  function variantPrices() {
    var dict = testDict();
    if (!dict) return null;

    var groups = assignedGroups();
    var map = {};
    var size = 0;

    Object.keys(dict).forEach(function (productId) {
      var product = dict[productId];
      /* The dict also carries `cssSettings`, `experiments`, `previewKey` and friends. */
      if (!product || !product.experimentId || !product.variantToGroupIndexToPrice) return;

      var groupIndex = groups[product.experimentId];
      if (typeof groupIndex !== 'number') return;

      var byVariant = product.variantToGroupIndexToPrice;
      Object.keys(byVariant).forEach(function (variantId) {
        var assigned = byVariant[variantId][groupIndex];
        var control = byVariant[variantId][0];
        if (!assigned || !control) return;

        map[String(variantId)] = {
          groupIndex: groupIndex,
          price: parseMoney(assigned.price),
          compareAt: assigned.compareAtPrice == null ? NaN : parseMoney(assigned.compareAtPrice),
          controlPrice: parseMoney(control.price),
          controlCompareAt: control.compareAtPrice == null ? NaN : parseMoney(control.compareAtPrice)
        };
        size++;
      });
    });

    return size ? map : null;
  }

  var cachedMap = null;
  var cachedKey = null;

  /** variantPrices() re-parses the whole dict; the result only changes when the assignment does. */
  function variantPricesCached() {
    var key = (localStorage.getItem(GROUP_KEY) || '') +
              (window.abconvertPriceCartTransformTestDict ? '|live' : '|stored') +
              /* The widget arrives after our first pass, and it flips which source wins. */
              (previewing() ? '|preview' : '');
    if (key !== cachedKey) {
      cachedKey = key;
      cachedMap = variantPrices();
    }
    return cachedMap;
  }

  /* ---------- PDP (Replo) ---------- */

  function reploScope() {
    return document.querySelector('[data-replo-product-container]');
  }

  function selectedVariantId(scope) {
    var input = scope.querySelector('form[action*="/cart/add"] [name="id"]');
    if (input && input.value) return String(input.value);

    var fromUrl = new URLSearchParams(location.search).get('variant');
    if (fromUrl) return fromUrl;

    var liquid = window.abconvertLiquidVariables;
    return liquid && liquid.shopifySelectedOrFirstAvailableVariantId
      ? String(liquid.shopifySelectedOrFirstAvailableVariantId)
      : null;
  }

  /**
   * Decide once per node whether it is the price or the compare-at, then remember it: after the
   * first rewrite the text no longer matches the control values we classified against, and Replo
   * re-renders these nodes whenever the variant changes.
   */
  function classify(el, entry, quantity) {
    var stored = el.getAttribute('data-stxnd-abc-role');
    if (stored) return stored;

    /* Replo scales both figures by the quantity, so match against the scaled control values. */
    var controlPrice = entry.controlPrice * quantity;
    var controlCompare = entry.controlCompareAt * quantity;

    var value = parseMoney(el.textContent);
    var role = null;

    if (isFinite(controlCompare) && controlCompare !== controlPrice && value === controlCompare) {
      role = 'compare';
    } else if (value === controlPrice) {
      role = 'price';
    } else if (getComputedStyle(el).textDecorationLine.indexOf('line-through') !== -1) {
      role = 'compare';
    }

    if (role) el.setAttribute('data-stxnd-abc-role', role);
    return role;
  }

  /**
   * Put back the figures an earlier pass wrote, when the group turns out to be the control after
   * all. Returning early is not enough on its own: the assignment can change under us — their
   * preview widget switches groups long after the page has settled — and a treatment price left
   * on screen would then be shown to a control visitor.
   *
   * Only nodes this bridge has already written to or hidden are touched. One it never stamped is
   * still carrying Liquid's own render, which is exactly what the control is supposed to see.
   */
  function restoreControl(scope, entry) {
    var quantityInput = scope.querySelector('[name="quantity"]');
    var quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;
    if (!isFinite(quantity) || quantity < 1) quantity = 1;

    var discounted = isFinite(entry.controlCompareAt) && entry.controlCompareAt > entry.controlPrice;

    Array.prototype.forEach.call(scope.querySelectorAll('.alchemy-rte'), function (el) {
      if (!el.getAttribute('data-stxnd-abc-applied') && !el.getAttribute('data-stxnd-abc-hidden')) return;

      var role = el.getAttribute('data-stxnd-abc-role');

      if (role === 'price') {
        show(el);
        setMoney(el, entry.controlPrice * quantity);
      } else if (role === 'compare') {
        if (discounted) {
          showCompare(el);
          setMoney(el, entry.controlCompareAt * quantity);
        } else {
          hideCompare(el);
        }
      } else if (PERCENT_BADGE.test(el.textContent.trim())) {
        if (discounted) {
          show(el);
          setPercent(el, savePercentReplo(entry.controlPrice, entry.controlCompareAt));
        } else {
          hide(el);
        }
      }
    });
  }

  function applyProductPage(map) {
    var scope = reploScope();
    if (!scope) return;

    var variantId = selectedVariantId(scope);
    var entry = variantId && map[variantId];
    if (!entry) return;

    /* Control renders exactly what Liquid produced, so there is nothing to wait for. Release the
       veil immediately rather than holding it on the 85% while ABConvert confirms the assignment. */
    if (entry.groupIndex === 0) {
      restoreControl(scope, entry);
      stopVeil();
      return;
    }

    /* Replo's PDP price is a line total, not a unit price — it renders price × quantity. Writing the
       unit price here would freeze the figure at $100 while the stepper climbed. */
    var quantityInput = scope.querySelector('[name="quantity"]');
    var quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;
    if (!isFinite(quantity) || quantity < 1) quantity = 1;

    var nodes = scope.querySelectorAll('.alchemy-rte');

    Array.prototype.forEach.call(nodes, function (el) {
      if (!isMoneyText(el.textContent.trim())) return;

      var role = classify(el, entry, quantity);
      if (!role) return;

      releaseVeil(el);

      if (role === 'price') {
        show(el);
        setMoney(el, entry.price * quantity);
      } else if (!isFinite(entry.compareAt) || entry.compareAt <= entry.price) {
        /* Scaling both by the same quantity leaves the comparison unchanged. */
        hideCompare(el);
      } else {
        showCompare(el);
        setMoney(el, entry.compareAt * quantity);
      }
    });

    /* Replo renders its own "SAVE n%" badge off the catalog compare-at, so it survives untouched
       and contradicts the group's pricing — 47% off next to a price with no compare-at at all. */
    var discounted = isFinite(entry.compareAt) && entry.compareAt > entry.price;
    var savings = discounted ? savePercentReplo(entry.price, entry.compareAt) : 0;

    Array.prototype.forEach.call(nodes, function (el) {
      if (!PERCENT_BADGE.test(el.textContent.trim())) return;

      releaseVeil(el);

      if (!discounted || savings <= 0) {
        hide(el);
      } else {
        show(el);
        setPercent(el, savings);
      }
    });
  }

  /* ---------- cart page and drawer ---------- */

  /** What a cart line is actually charged per unit, after any cart- or line-level discount. */
  function chargedUnitPrice(row) {
    var wrapper = row.querySelector('.cart-items__unit-price-wrapper');
    if (!wrapper) return NaN;

    var candidates = wrapper.querySelectorAll('span, div');
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      if (el.children.length) continue;
      if (el.classList.contains('visually-hidden')) continue;
      if (el.closest('s')) continue;               /* that one is the strike-through */

      var value = parseMoney(el.textContent);
      if (isFinite(value)) return value;
    }
    return NaN;
  }

  /**
   * The "was" price for a cart line, in order of precedence:
   *
   *   1. the group's compare-at, when the group actually carries one above its price;
   *   2. otherwise the group's own price, *if a discount has taken the line below it* — a BOGO or
   *      similar leaves the line charging less than the test price, and with no strike-through the
   *      saving is invisible and the discount reads as the regular price;
   *   3. otherwise nothing, and the strike-through is hidden.
   *
   * The transform sets the undiscounted line price to the group price, so a charged price below it
   * is exactly the signal that a discount is in play.
   */
  function wasPriceForLine(entry, row) {
    if (isFinite(entry.compareAt) && entry.compareAt > entry.price) return entry.compareAt;

    var charged = chargedUnitPrice(row);
    if (isFinite(charged) && isFinite(entry.price) && charged < entry.price) return entry.price;

    return NaN;
  }

  function applyCart(map) {
    var rows = document.querySelectorAll('[data-key]');

    Array.prototype.forEach.call(rows, function (row) {
      /* A line item key is "<variantId>:<hash>". */
      var variantId = (row.getAttribute('data-key') || '').split(':')[0];
      if (!/^\d+$/.test(variantId)) return;

      var entry = map[variantId];
      if (!entry) return;

      var was = wasPriceForLine(entry, row);

      Array.prototype.forEach.call(row.querySelectorAll('.compare-at-price'), function (el) {
        el.setAttribute('data-stxnd-abc-role', 'compare');

        if (!isFinite(was)) {
          hideCompare(el);
        } else {
          showCompare(el);
          setMoney(el, was);
        }
      });
    });
  }

  /* ---------- the in-drawer upsell card ---------- */

  /**
   * `snippets/cart-upsell.liquid` renders the upsell's price, compare-at and "SAVE n%" from catalog
   * data, so the card offers the control pricing to a treatment visitor.
   *
   * The swatch buttons matter as much as the visible figures: `assets/cart-upsell.js` copies
   * `data-price` / `data-compare` / `data-save` straight into the card when a colour is picked, so
   * leaving those alone would restore catalog pricing on the first swatch click. Emptying a dataset
   * value is deliberate — that JS guards on it being truthy, so a blank one means "leave it".
   */
  function applyCartUpsell(map) {
    Array.prototype.forEach.call(document.querySelectorAll('.cart-upsell'), function (card) {
      var addButton = card.querySelector('[data-cart-upsell-add]');
      var entry = addButton && map[addButton.getAttribute('data-variant-id')];

      if (entry) {
        var discounted = isFinite(entry.compareAt) && entry.compareAt > entry.price;

        var priceEl = card.querySelector('[data-upsell-price]');
        if (priceEl) setMoney(priceEl, entry.price);

        var compareEl = card.querySelector('[data-upsell-compare]');
        if (compareEl) {
          if (discounted) {
            showCompare(compareEl);
            setMoney(compareEl, entry.compareAt);
          } else {
            hideCompare(compareEl);
          }
        }

        var saveEl = card.querySelector('[data-upsell-save]');
        if (saveEl) {
          if (discounted) {
            show(saveEl);
            setPercent(saveEl, savePercentTheme(entry.price, entry.compareAt));
          } else {
            hide(saveEl);
          }
        }
      }

      /* Each swatch carries its own variant, so retarget them individually. */
      Array.prototype.forEach.call(card.querySelectorAll('[data-upsell-swatch]'), function (swatch) {
        var swatchEntry = map[swatch.getAttribute('data-variant-id')];
        if (!swatchEntry) return;

        var keeps = isFinite(swatchEntry.compareAt) && swatchEntry.compareAt > swatchEntry.price;

        if (swatch.dataset.price) {
          var nextPrice = formatLike(swatch.dataset.price, swatchEntry.price);
          if (nextPrice != null) swatch.dataset.price = nextPrice;
        }
        swatch.dataset.compare = keeps
          ? (formatLike(swatch.dataset.compare || swatch.dataset.price || '$0', swatchEntry.compareAt) || '')
          : '';
        swatch.dataset.save = keeps
          ? String(savePercentTheme(swatchEntry.price, swatchEntry.compareAt))
          : '';
      });
    });
  }

  /* ---------- cart summary totals ---------- */

  /** The server-rendered value, remembered so repeated passes never compound their own correction. */
  function serverValue(el) {
    var stored = el.getAttribute('data-stxnd-abc-server');
    if (stored !== null) return parseFloat(stored);
    var value = parseMoney(el.textContent);
    el.setAttribute('data-stxnd-abc-server', String(value));
    return value;
  }

  /**
   * `snippets/cart-summary.liquid` builds the savings total, the struck-through regular total and
   * the percent-off badge from `item.variant.compare_at_price` — catalog data the test changes. So a
   * treatment cart claims "40% OFF, was $500" while its own line items add up to a $350 regular
   * total. Correct all three by the difference between the group's compare-at markdown and the
   * control's, which is the only term that moved; discount savings and non-test lines carry through.
   */
  function applyCartSummary(map) {
    var savingsEl = document.querySelector('.cart-savings__total');
    var compareEl = document.querySelector('.cart-totals__compare');
    if (!savingsEl && !compareEl) return;

    var deltaCents = 0;

    Array.prototype.forEach.call(document.querySelectorAll('[data-key]'), function (row) {
      var entry = map[(row.getAttribute('data-key') || '').split(':')[0]];
      if (!entry) return;

      var input = row.querySelector('input[name="updates[]"]');
      var quantity = input ? parseInt(input.value, 10) : 1;
      if (!isFinite(quantity) || quantity < 1) quantity = 1;

      /* The cart transform sets the line price to the group price, and that is exactly what Liquid
         measured each markdown against (`item.original_price`). */
      var lineCents = Math.round(entry.price * 100);
      var group = isFinite(entry.compareAt)
        ? Math.max(0, Math.round(entry.compareAt * 100) - lineCents) : 0;
      var control = isFinite(entry.controlCompareAt)
        ? Math.max(0, Math.round(entry.controlCompareAt * 100) - lineCents) : 0;

      deltaCents += (group - control) * quantity;
    });

    if (deltaCents === 0) return;

    var savingsCents = savingsEl ? Math.round(serverValue(savingsEl) * 100) + deltaCents : 0;
    var regularCents = compareEl ? Math.round(serverValue(compareEl) * 100) + deltaCents : 0;

    /* Nothing left to crow about: drop the badge, the strike-through and the savings breakdown. */
    if (savingsCents <= 0) {
      var accordion = savingsEl && (savingsEl.closest('.cart-savings') || savingsEl.closest('.cart-savings__details'));
      if (accordion) hide(accordion);
      if (compareEl) hide(compareEl);
      var zeroBadge = document.querySelector('.cart-totals__badge');
      if (zeroBadge) hide(zeroBadge);
      return;
    }

    if (savingsEl) setMoney(savingsEl, savingsCents / 100);
    if (compareEl) setMoney(compareEl, regularCents / 100);

    /* First breakdown row is the markdowns line (Liquid renders it before discounts). */
    var row = document.querySelector('.cart-savings__row');
    if (row) {
      var rowValue = row.children.length ? row.children[row.children.length - 1] : null;
      if (rowValue) {
        var rowCents = Math.round(serverValue(rowValue) * 100) + deltaCents;
        if (rowCents > 0) setMoney(rowValue, rowCents / 100);
        else hide(row);
      }
    }

    var badge = document.querySelector('.cart-totals__badge');
    if (badge && regularCents > 0) {
      /* Mirror the Liquid: integer division with half the divisor added to round to nearest. */
      var percent = Math.floor((savingsCents * 100 + Math.floor(regularCents / 2)) / regularCents);
      if (percent > 0) {
        show(badge);
        setPercent(badge, percent);
      } else {
        hide(badge);
      }
    }
  }

  /* ---------- product cards anywhere ---------- */

  /** Nearest selling price for a compare-at node, searching outward through its price block. */
  function sellingPriceNear(compareEl) {
    var node = compareEl.parentElement;

    for (var depth = 0; node && depth < 4; depth++, node = node.parentElement) {
      var candidates = node.querySelectorAll('.price, .price-item--sale');

      for (var i = 0; i < candidates.length; i++) {
        var candidate = candidates[i];
        if (candidate === compareEl || compareEl.contains(candidate) || candidate.contains(compareEl)) continue;
        if (candidate.classList.contains('compare-at-price') || candidate.closest('s')) continue;

        var value = parseMoney(candidate.textContent);
        if (isFinite(value)) return value;
      }

      /* Some markup leaves the selling price as a bare text node beside the struck-out one,
         e.g. `<div><s>$150.00</s> $100.00</div>` — no element to select. */
      for (var j = 0; j < node.childNodes.length; j++) {
        var child = node.childNodes[j];
        if (child.nodeType !== 3) continue;
        var direct = parseMoney(child.nodeValue);
        if (isFinite(direct)) return direct;
      }
    }

    return NaN;
  }

  /**
   * A crossed-out price at or below the selling price is never right. The theme's own Liquid
   * already guards this, so in practice this only catches the markup ABConvert injects into
   * product cards for a group whose compare-at equals its price.
   */
  function hideRedundantCompareAt() {
    var compares = document.querySelectorAll('.compare-at-price, .abconvert-product-price s');

    Array.prototype.forEach.call(compares, function (el) {
      /* Lines already governed by applyProductPage / applyCart. */
      if (el.getAttribute('data-stxnd-abc-role')) return;

      /* ABConvert's preview panel lists the test's own prices in this same markup. It is theirs and
         is never shown to a shopper — leave it alone. */
      if (el.closest('.abconvert-widget')) return;

      /* When a group carries no compare price, ABConvert hides the compare-at by setting
         display:none on it and leaves its stale text in place. The theme's "Regular price" label
         sits in a separate `.visually-hidden` sibling that nobody hid, so a screen reader reads
         "Sale price $100.00, Regular price" and then stops. Take the label with it.
         Checking the element's OWN computed display is deliberate: an element inside a hidden
         ancestor still reports its own display, so a collapsed drawer or carousel slide does not
         get its labels hidden permanently. */
      if (getComputedStyle(el).display === 'none') {
        hideCompare(el);
        return;
      }

      var compare = parseMoney(el.textContent);

      /* Same situation, for the variant of it where the element is blanked rather than hidden. */
      if (!isFinite(compare)) {
        hideCompare(el);
        return;
      }

      var price = sellingPriceNear(el);
      if (!isFinite(price)) return;

      if (compare <= price) hideCompare(el);
      else showCompare(el);
    });
  }

  /**
   * ABConvert hides the selling price it has just written whenever the assigned group carries no
   * compare-at: it stamps the node as processed, writes the group's price into it, and then sets
   * `display: none` on the node itself — so the treatment for such a product renders a card with
   * no price at all. Verified in preview against this theme: the stand's home-page card in
   * Variant A held `<span class="price" data-processed-abconvert-v2-product-card-price
   * style="display: none;">$90.00</span>`, while the bundle — which keeps a compare-at in that
   * same group — was left alone.
   *
   * Clearing the inline rule is safe here: the theme picks between its regular and its sale price
   * block with the `price__hidden` CLASS on the wrapper, never with an inline style on the price
   * itself, so whichever block Liquid meant to show is still the one that shows.
   *
   * Only nodes ABConvert has stamped as processed are touched, so their own anti-flicker veil —
   * which covers nodes they have not written to yet — is left to release itself.
   */
  function isProcessedPriceNode(el) {
    var names = el.getAttributeNames();

    for (var i = 0; i < names.length; i++) {
      /* Their marker carries a version: `data-processed-abconvert-v2-product-card-price`. */
      if (names[i].indexOf('data-processed-abconvert') === 0 && names[i].indexOf('price') !== -1) {
        return true;
      }
    }

    return false;
  }

  function revealHiddenCardPrices() {
    Array.prototype.forEach.call(document.querySelectorAll('.price'), function (el) {
      if (el.style.display !== 'none') return;

      if (el.getAttribute('data-stxnd-abc-hidden')) return;  /* ours, and deliberate */
      if (el.classList.contains('compare-at-price')) return; /* a "was" price, not a selling one */
      if (el.closest('.abconvert-widget')) return;           /* their preview panel */

      if (!isMoneyText(el.textContent.trim())) return;
      if (!isProcessedPriceNode(el)) return;

      el.style.removeProperty('display');
    });
  }

  /* ---------- scheduling ---------- */

  var observer = null;
  var queued = false;

  /**
   * Correct the PDP price *synchronously*, from inside the MutationObserver callback. That callback
   * runs as a microtask — before the browser paints — so a catalog value Replo has just written is
   * replaced in the same frame and never reaches the screen. The veil covers the very first paint,
   * before any of our JS has run; this covers every re-render after it, including the ones a
   * quantity or variant change triggers long after the veil has been released.
   */
  function correctProductPageNow() {
    if (observer) observer.disconnect();
    try {
      var map = variantPricesCached();
      if (map) applyProductPage(map);
    } catch (error) {
      stopVeil();
      if (window.console) console.warn('[stxnd-abconvert-price]', error);
    } finally {
      connect();
    }
  }

  function connect() {
    if (!observer) {
      observer = new MutationObserver(function () {
        correctProductPageNow();
        /* Cart, summary and card work is not flash-sensitive — coalesce it onto the timer. */
        schedule();
      });
    }
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  }

  /* setTimeout, not requestAnimationFrame: rAF is paused in a background tab, which would leave a
     PDP opened in a new tab showing the control price until it was focused. */
  function schedule() {
    if (queued) return;
    queued = true;
    setTimeout(function () {
      queued = false;
      apply();
    }, 16);
  }

  function apply() {
    /* Detach first so our own writes do not re-trigger the observer. */
    if (observer) observer.disconnect();

    try {
      var map = variantPricesCached();
      if (map) {
        applyProductPage(map);
        applyCart(map);
        applyCartUpsell(map);
        applyCartSummary(map);
      }
      revealHiddenCardPrices();
      hideRedundantCompareAt();
    } catch (error) {
      /* Never leave a page with its prices veiled because of a bug in here. */
      stopVeil();
      if (window.console) console.warn('[stxnd-abconvert-price]', error);
    } finally {
      connect();
    }
  }

  var SWEEPS = [0, 150, 500, 1200, 2500];

  function start() {
    /* Fast path: localStorage already holds the assignment and prices on any repeat view. */
    apply();

    /* Replo renders the compare-at and badge client-side, after this point. The observer catches
       that, but these sweeps make it deterministic rather than dependent on mutation timing. */
    SWEEPS.forEach(function (delay) {
      setTimeout(apply, delay);
    });
    window.addEventListener('load', apply);
    window.addEventListener('pageshow', apply);

    var api = window.abconvert;
    if (api && api.utils && typeof api.utils.onReady === 'function') {
      try {
        api.utils.onReady(apply);
        return;
      } catch (error) {
        /* fall through to polling */
      }
    }

    /* Their runtime may still be loading; give it ~2s to arrive, then stop asking. */
    var tries = 0;
    (function poll() {
      var utils = window.abconvert && window.abconvert.utils;
      if (utils && typeof utils.onReady === 'function') {
        try {
          utils.onReady(apply);
        } catch (error) {
          apply();
        }
        return;
      }
      if (++tries > 40) return;
      setTimeout(poll, 50);
    })();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
